import { timelinePhases } from "@/mocks/scandal";

const phaseColor = [
  { dot: "bg-secondary-500 ring-secondary-200", chip: "bg-secondary-100 text-secondary-800", num: "text-secondary-200" },
  { dot: "bg-primary-500 ring-primary-200", chip: "bg-primary-100 text-primary-800", num: "text-primary-100" },
  { dot: "bg-accent-500 ring-accent-200", chip: "bg-accent-100 text-accent-800", num: "text-accent-100" },
  { dot: "bg-foreground-800 ring-background-300", chip: "bg-background-200 text-foreground-700", num: "text-background-200" },
];

export default function TimelineSection() {
  return (
    <section id="timeline" className="relative overflow-hidden border-b border-background-200/70 bg-background-100/50">
      <div className="mx-auto max-w-6xl px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2">
            <span className="inline-block h-4 w-1.5 rounded-sm bg-primary-500" />
            <span className="text-xs font-bold uppercase tracking-widest text-primary-600">Main Timeline</span>
          </div>
          <h2 className="font-heading mt-2 text-2xl font-black text-foreground-950 md:text-3xl">
            抖音黑料时间线 · 从发酵到回应的核心脉络
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            下面把一次典型抖音黑料的抖音黑料全记录按顺序展开：先有哪条线索、如何扩散、当事人何时回应、平台几时介入，
            节点一个不落，吃瓜不用再自己拼。
          </p>
        </div>

        {/* 阶段节点 */}
        <div className="relative mt-10">
          {/* 竖向引导线 */}
          <div className="absolute bottom-6 left-[11px] top-2 w-px bg-background-300 md:left-1/2" />
          <div className="space-y-10 md:space-y-14">
            {timelinePhases.map((phase, pi) => {
              const c = phaseColor[pi % phaseColor.length];
              return (
                <div key={phase.date} className="relative grid grid-cols-[24px_1fr] gap-4 md:grid-cols-2 md:gap-10">
                  {/* 标记点 */}
                  <div className="relative z-10 flex items-start md:order-1">
                    <span className={`mt-1 inline-block h-3.5 w-3.5 rounded-full ring-4 ${c.dot}`} />
                  </div>

                  {/* 阶段头 */}
                  <div className="md:order-2 md:col-start-2">
                    <div className="mb-3 flex items-center gap-3 md:mb-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${c.chip}`}>{phase.date}</span>
                      <span className="hidden text-xs text-foreground-400 md:inline">第 {pi + 1} 阶段 · 节点 {phase.nodes.length} 处</span>
                    </div>
                  </div>

                  {/* 节点卡片 */}
                  <div className="col-span-2 md:col-span-2 md:col-start-2">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {phase.nodes.map((n, ni) => (
                        <article
                          key={ni}
                          className="flex rounded-lg border border-background-200 bg-background-50 p-4"
                        >
                          <div className="flex flex-1 flex-col">
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] font-bold text-foreground-400">{String(ni + 1).padStart(2, "0")}</span>
                              <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${c.chip}`}>{n.time}</span>
                            </div>
                            <h3 className="mt-2 text-[15px] font-bold text-foreground-900">{n.title}</h3>
                            <p className="mt-1.5 flex-1 text-sm leading-relaxed text-foreground-600">{n.desc}</p>
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 底部：一图看懂前因后果 */}
        <div className="relative mt-12 overflow-hidden rounded-xl">
          <img
            src="https://readdy.ai/api/search-image?query=Wide%20editorial%20flat%20illustration%20of%20a%20scrolling%20timeline%20with%20connected%20nodes%2C%20folders%20and%20archive%20boxes%20along%20a%20winding%20red%20line%2C%20crimson%20and%20amber%20on%20cream%20background%2C%20minimal%20magazine%20style%2C%20soft%20and%20layered%2C%20high%20detail&width=1400&height=700&seq=heiliao-breakout-2026&orientation=landscape"
            alt="抖音黑料 前因后果脉络一图看懂示意"
            title="抖音黑料 前因后果脉络一图看懂示意"
            className="h-52 w-full object-cover object-top md:h-64"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/70 via-background-950/30 to-transparent" />
          <div className="absolute inset-y-0 left-0 flex flex-col justify-center px-5 md:px-8">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-background-50">
              <i className="ri-map-2-line" /> 脉络纵览
            </span>
            <h3 className="font-heading mt-3 max-w-sm text-xl font-black leading-snug text-background-50 md:text-2xl">
              前因后果，一条线看完
            </h3>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-background-100/90">
              从头到尾的关系都帮你接好了，直接读，省得自己翻聊天记录和评论区拼瓜。
            </p>
            <a
              href="#archive"
              className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-md bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-950 whitespace-nowrap hover:bg-background-100"
            >
              进入归档资源
              <i className="ri-download-2-line" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}